import moment from 'moment'
import { GroupBy } from 'app/components/viz/DateGroupSelector'
import { formatDate } from 'app/date.utils'
import { ActivityJson } from 'app/types/entities'
import { isScheduledCall } from './useAiAgentVoice'

export const SERIES_KEY = `New Patient Calls`

export const SCHEDULED_KEY = `Scheduled`
export const NOT_SCHEDULED_KEY = `Not Scheduled`
// Chart hues (app/lib/chartColors) matching the table's tag badges: green = scheduled, orange = not scheduled.
export const SCHEDULED_COLOR = `green`
export const NOT_SCHEDULED_COLOR = `orange`

export type OutcomeBucket = { label: string; tooltip: string; scheduled: number; notScheduled: number; total: number }

const MOMENT_UNIT: Record<GroupBy, moment.unitOfTime.StartOf> = {
  days: `day`,
  weeks: `week`,
  months: `month`,
}

/** Ordered YYYY-MM-DD bucket keys spanning the range for the given grouping. */
export function seedBucketKeys(fromDate: string, toDate: string, groupBy: GroupBy): string[] {
  const unit = MOMENT_UNIT[groupBy]
  // Bucket in UTC to match how the rest of the dashboard renders call times
  // (the activities table formats `activityDate` via `formatDate`, which is UTC).
  const start = moment.utc(fromDate).startOf(unit)
  const end = moment.utc(toDate).endOf(unit)
  const keys: string[] = []
  for (let cursor = start.clone(); cursor.isSameOrBefore(end); cursor.add(1, groupBy)) {
    keys.push(cursor.format(`YYYY-MM-DD`))
  }
  return keys
}

// Format bucket date keys through the shared `formatDate` util (used by the
// marketing dashboards) for consistent date labels. Keys are date-only strings,
// so `formatDate` renders them deterministically regardless of timezone.
function fmt(value: string, format: string): string {
  return formatDate({ value, dateOnly: true, format }) ?? value
}

export function labelFor(key: string, groupBy: GroupBy): { label: string; tooltip: string } {
  const weekEnd = moment.utc(key).endOf(`week`).format(`YYYY-MM-DD`)
  const label = groupBy === `months` ? fmt(key, `MMM YYYY`) : fmt(key, `MMM D`)
  const tooltip =
    groupBy === `weeks`
      ? `${fmt(key, `MMM D`)} - ${fmt(weekEnd, `MMM D`)}`
      : groupBy === `months`
        ? fmt(key, `MMMM YYYY`)
        : fmt(key, `MMM D, YYYY`)
  return { label, tooltip }
}

function bucketKeyFor(activity: ActivityJson, groupBy: GroupBy): string | null {
  if (!activity.activityDate) return null
  return moment.utc(activity.activityDate).startOf(MOMENT_UNIT[groupBy]).format(`YYYY-MM-DD`)
}

/** Buckets qualified New Patient calls split into Scheduled vs Not Scheduled. */
export function bucketByScheduled(
  activities: ActivityJson[],
  fromDate: string,
  toDate: string,
  groupBy: GroupBy,
): OutcomeBucket[] {
  const buckets = new Map<string, { scheduled: number; notScheduled: number }>(
    seedBucketKeys(fromDate, toDate, groupBy).map((k) => [k, { scheduled: 0, notScheduled: 0 }]),
  )

  for (const activity of activities) {
    const key = bucketKeyFor(activity, groupBy)
    if (!key || !buckets.has(key)) continue
    const bucket = buckets.get(key)!
    if (isScheduledCall(activity)) bucket.scheduled += 1
    else bucket.notScheduled += 1
  }

  return [...buckets.entries()].map(([key, { scheduled, notScheduled }]) => {
    const { label, tooltip } = labelFor(key, groupBy)
    return { label, tooltip, scheduled, notScheduled, total: scheduled + notScheduled }
  })
}

/**
 * Where a trend chart's bars stop and whether the last one is still filling up. Bars run
 * through `today` (YYYY-MM-DD, in the same day space the chart buckets in), never past
 * it; when the range includes today, the last bucket (today, this week, this month) is
 * in progress.
 */
export function inProgressRange(
  fromDate: string,
  toDate: string,
  today: string,
): { lastDay: string; lastBarInProgress: boolean } {
  return {
    lastDay: toDate < today ? toDate : today,
    lastBarInProgress: fromDate <= today && toDate >= today,
  }
}

export type GroupControls = {
  disableDays: boolean
  disableWeeks: boolean
  disableMonths: boolean
  defaultGroup: GroupBy
}

/** Which grouping options make sense for a given range, and the sensible default. */
export function groupControlsForRange(fromDate: string, toDate: string): GroupControls {
  const rangeDays = Math.max(1, moment.utc(toDate).diff(moment.utc(fromDate), `days`) + 1)
  // Gate the aggregation options by range, matching the Marketing dashboards
  // (LeadsGraph / ScheduleRateGraph): days past 90 days, weeks under a week or
  // past 630 days, months for a month or less. Default to the coarsest grouping
  // that's still enabled for the range.
  return {
    disableDays: rangeDays > 90,
    disableWeeks: rangeDays > 630 || rangeDays <= 7,
    disableMonths: rangeDays <= 31,
    defaultGroup: rangeDays > 630 ? `months` : rangeDays > 90 ? `weeks` : `days`,
  }
}

/** Clamps a chosen grouping to one that's valid (enabled) for the current range. */
export function resolveGroup(group: GroupBy, controls: GroupControls): GroupBy {
  const disabled: Record<GroupBy, boolean> = {
    days: controls.disableDays,
    weeks: controls.disableWeeks,
    months: controls.disableMonths,
  }
  return disabled[group] ? controls.defaultGroup : group
}
